import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: "How accurate is this score?",
    answer: "Your score is calculated using live data from ChatGPT and Google Search results, weighted by academic research from KDD, NeurIPS, and supporting preprints. AI responses can vary between sessions. Your score represents a point-in-time snapshot, not a permanent ranking.",
  },
  {
    question: "Why doesn't my brand appear in AI responses?",
    answer: "AI platforms choose sources differently than Google. Research shows that 37% of domains cited by AI are unique. They don't overlap with Google's organic results. Ranking on Google is necessary but not sufficient. AI visibility requires additional optimization like structured data, topical authority, and platform-specific signals.",
  },
  {
    question: "How can I improve my AI visibility?",
    answer: "Start with the AI-Readiness Check above: it identifies the technical and content gaps that matter most. The highest-impact actions are typically ensuring AI crawlers can access your site, adding structured data, and building topical authority through comprehensive content. Traditional SEO remains the foundation. Research shows it yields 7× more visibility improvement than GEO-only tactics.",
  },
];

const ResultsFAQ = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  return (
    <div className="w-full max-w-[900px] mx-auto space-y-4 md:space-y-6 animate-fade-in">
      <h3 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
        <HelpCircle className="w-5 h-5 md:w-6 md:h-6 text-primary" />
        Frequently Asked Questions
      </h3>
      
      <div className="space-y-2">
        {faqItems.map((item, index) => {
          const isExpanded = expandedIndex === index;
          
          return (
            <div
              key={index}
              className={cn(
                "rounded-lg border border-border bg-card/50 backdrop-blur-sm overflow-hidden transition-all duration-300",
                isExpanded && "shadow-md border-primary/30"
              )}
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors min-h-[56px]"
                aria-expanded={isExpanded}
              >
                <span className="font-medium text-foreground pr-4">{item.question}</span>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-300",
                    isExpanded && "rotate-180 text-primary"
                  )}
                />
              </button>
              
              <div
                className={cn(
                  "grid transition-all duration-300 ease-out",
                  isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                )}
              >
                <div className="overflow-hidden">
                  <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
                    {item.answer === "LINK_ITEM" ? (
                      <>
                        AILens is a completed research project (Jan-Feb 2026) implementing insights from published AI visibility studies. See our{" "}
                        <Link to="/methodology" state={{ fromResults: true }} className="text-primary hover:underline">
                          methodology page
                        </Link>{" "}
                        for details on the scoring approach.
                      </>
                    ) : (
                      item.answer
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default ResultsFAQ;
